import { FC, PropsWithChildren, createContext, useState } from "react";
import { BucketContextType, SortProperty, SortStatus } from "./types";
import { S3, AWSError } from "aws-sdk";
import { BUCKET_NAME, MAPPED_MIME_TYPE, REGION } from "../../config";
import { useAppDispatch, useAppSelector } from "redux/store";
import { selectLogin } from "redux/selectors";
import { Session } from "types";
import { getSummaryDetails, isExpiredData } from "utils";
import { getSession } from "api";
import { setSession } from "redux/slices";
import { convertToContents, sortContent } from "./utils";
import { ListObjectsV2Output } from "aws-sdk/clients/s3";
import { PromiseResult } from "aws-sdk/lib/request";
import { PreviewModalConfig } from "pages/home/components/contentController/types";

const defaultValue: BucketContextType = {
  content: undefined,
  bucketSummary: undefined,
  loading: false,
  breadcrumb: "",
  apiHandler: async () => {},
  // Utils
  sortStatus: { property: "name", order: "asc" },
  handleSortByProperty: (property: SortProperty) => {},
  // API
  fetchHomePageData: async () => true,
  fetchBucketSummary: async () => true,
  fetchBucket: async () => undefined,
  refreshContents: async () => true,
  // File
  checkFileExist: async (file: File) => true,
  uploadFiles: async (files: File[]) => ({
    successFilesUploaded: [],
    failureFilesUploaded: [],
  }),
  deleteFile: async (fullPath: string, thumbnail: string | undefined) => true,
  renameFile: async () => true,
  getPresignedUrl: async (config: PreviewModalConfig) => undefined,
  // Folder
  createFolder: async () => true,
  deleteFolder: async () => true,
  renameFolder: async () => true,
};

export const BucketContext = createContext<BucketContextType>(defaultValue);

export const BucketContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [content, setContent] = useState(defaultValue.content);
  const [bucketSummary, setBucketSummary] = useState(
    defaultValue.bucketSummary
  );
  const [breadcrumb, setBreadcrumb] = useState(defaultValue.breadcrumb);
  const [loading, setLoading] = useState(false);
  const [sortStatus, setSortStatus] = useState(defaultValue.sortStatus);

  const { guid, session } = useAppSelector(selectLogin);
  const dispatch = useAppDispatch();

  const setBucketConnection = (guid: string, session: Session) => {
    const paramsBucket = { Bucket: BUCKET_NAME, Key: guid };
    const { Key, Secret, SessionToken } = session;
    const bucket = new S3({
      params: paramsBucket,
      accessKeyId: Key,
      secretAccessKey: Secret,
      sessionToken: SessionToken,
      region: REGION,
      sslEnabled: true,
    });
    return bucket;
  };

  const refreshBucket = async () => {
    if (guid && session) {
      const { Expiration } = session;
      const haveToReload = isExpiredData(Expiration);
      if (haveToReload) {
        const response = await getSession(guid);
        if (response) {
          if (response.resposeStatus.ok) {
            const newSession = response.response;
            const bucket = setBucketConnection(guid, newSession);
            dispatch(setSession(newSession));
            return bucket;
          }
        }
      } else {
        const bucket = setBucketConnection(guid, session);
        return bucket;
      }
    }
    return null;
  };

  const apiHandler = async (
    apiCall: (...args: unknown[]) => Promise<unknown>
  ) => {
    setLoading(true);
    const result: Awaited<ReturnType<typeof apiCall>> = await apiCall();
    setLoading(false);
    return result;
  };

  const deleteThumbnail = async (thumbnail: string) => {
    const exist = await checkFileExistByPath(thumbnail);
    if (exist) {
      var params2 = { Bucket: BUCKET_NAME, Key: thumbnail };
      const bucket = await refreshBucket();
      try {
        await bucket?.deleteObject(params2).promise();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const cleanObsoleteThumbnails = async (obsoleteThumbnails: string[]) => {
    for (let path of obsoleteThumbnails) {
      await deleteThumbnail(path);
    }
  };

  const fetchHomePageData = async (subfolder?: string) => {
    const Prefix = subfolder || `${guid}/`;
    setBreadcrumb(Prefix);
    const params2 = {
      Bucket: BUCKET_NAME,
      Prefix,
      StartAfter: Prefix,
      Delimiter: "/",
      MaxKeys: 2147483647, // Maximum allowed by S3 API
    };
    const bucket = await refreshBucket();
    try {
      const response = await bucket?.listObjectsV2(params2).promise();
      if (response) {
        const { data, error } = response.$response;
        if (data) {
          const { obsoleteThumbnailsPath, ...content } =
            convertToContents(data);
          const sortedContent = sortContent(content, sortStatus);
          cleanObsoleteThumbnails(obsoleteThumbnailsPath); // we don't wait the end of this operation, but just we put in on background
          setContent(sortedContent);
          return true;
        }
        return Boolean(error);
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const fetchBucket = async () => {
    const Prefix = `${guid}/`;
    const params2 = {
      Bucket: BUCKET_NAME,
      Prefix,
      StartAfter: Prefix,
      MaxKeys: 2147483647,
    };
    let result = undefined;
    const bucket = await refreshBucket();
    try {
      const response = await bucket?.listObjectsV2(params2).promise();
      if (response?.$response?.data) {
        result = response.$response.data as ListObjectsV2Output;
      }
    } catch (error) {
    } finally {
      return result;
    }
  };

  const fetchBucketSummary = async () => {
    const Prefix = `${guid}/`;
    const params2 = {
      Bucket: BUCKET_NAME,
      Prefix,
      StartAfter: Prefix,
      MaxKeys: 2147483647,
    };
    const bucket = await refreshBucket();
    try {
      const response = await bucket?.listObjectsV2(params2).promise();
      if (response) {
        const { data } = response.$response;
        if (data) {
          const summary = getSummaryDetails(data?.Contents ?? []);
          setBucketSummary(summary);
          return true;
        }
      }
      return false;
    } catch (error) {
      return false;
      // unhandle error
    }
  };

  const checkFileExistByPath = async (path: string) => {
    const params = { Bucket: BUCKET_NAME, Key: path };
    const bucket = await refreshBucket();
    try {
      await bucket?.headObject(params).promise();
      return true;
    } catch (error) {
      return false;
    }
  };

  const checkFileExist = async (file: File) => {
    const Key = `${breadcrumb}${file.name}`;
    const params = { Bucket: BUCKET_NAME, Key };
    const bucket = await refreshBucket();
    try {
      await bucket?.headObject(params).promise();
      return true;
    } catch (error) {
      return false;
    }
  };

  const getPresignedUrl = async (config: PreviewModalConfig) => {
    const Key = `${breadcrumb}${config.fileName}`;
    const params = {
      Bucket: BUCKET_NAME,
      Key,
      ResponseContentDisposition: "inline",
      ResponseContentType: MAPPED_MIME_TYPE[config.fileExtension],
    };

    const bucket = await refreshBucket();
    try {
      const result = bucket?.getSignedUrl("getObject", params);
      return result;
      // if (result) {
      //   const res = await fetch(result);
      //   console.log(res);
      // }
    } catch (error) {
      console.log(error);
    }
  };

  const uploadFile = async (file: File) => {
    const Key = `${breadcrumb}${file.name}`;
    const params2 = { Bucket: BUCKET_NAME, Key, Body: file };
    const bucket = await refreshBucket();
    try {
      await bucket?.upload(params2).promise();
      return true;
    } catch (error) {
      return false;
    }
  };

  const uploadFiles = async (files: File[]) => {
    const successFilesUploaded: File[] = [];
    const failureFilesUploaded: File[] = [];
    for (let file of files) {
      const result = await uploadFile(file);
      result
        ? successFilesUploaded.push(file)
        : failureFilesUploaded.push(file);
    }
    return { successFilesUploaded, failureFilesUploaded };
  };

  const deleteFile = async (
    fullPath: string,
    thumbnail: string | undefined
  ) => {
    thumbnail && (await deleteThumbnail(thumbnail));
    var params2 = { Bucket: BUCKET_NAME, Key: fullPath };
    const bucket = await refreshBucket();
    try {
      await bucket?.deleteObject(params2).promise();
      return true;
    } catch (error) {
      return false;
    }
  };

  const refreshContents = async () => {
    try {
      const responseFetchHomePage = await fetchHomePageData(breadcrumb);
      const responseFetchSummary = await fetchBucketSummary();
      return responseFetchHomePage && responseFetchSummary;
    } catch (error) {
      return false;
    }
  };

  const createFolder = async (folderName: string) => {
    const params2 = { Bucket: BUCKET_NAME, Key: `${breadcrumb}${folderName}/` };
    const bucket = await refreshBucket();
    try {
      const response = await bucket?.putObject(params2).promise();
      return Boolean(response);
    } catch (error) {
      return false;
    }
  };

  const deleteFolder = async (path: string): Promise<boolean> => {
    const params2 = { Bucket: BUCKET_NAME, Prefix: path };
    const bucket = await refreshBucket();
    try {
      const response = await bucket?.listObjectsV2(params2).promise();
      if (response) {
        const { data } = response.$response;
        if (data) {
          const objects = data.Contents?.map((obj) => ({
            Key: obj.Key,
          })) as AWS.S3.ObjectIdentifierList;

          const params3 = {
            Bucket: BUCKET_NAME,
            Delete: { Objects: objects, Quiet: true },
          };
          const result = await bucket?.deleteObjects(params3).promise();
          return Boolean(result);
        }
      }
    } catch (error) {
      return false;
    }

    return false;
  };

  const copyFolder = async (sourcePath: string, destinationPath: string) => {
    try {
      const params2 = { Bucket: BUCKET_NAME, Prefix: sourcePath };
      const bucket = await refreshBucket();

      const promises: Promise<PromiseResult<S3.CopyObjectOutput, AWSError>>[] =
        [];
      const response = await bucket?.listObjectsV2(params2).promise();
      if (response) {
        const { data } = response.$response;
        if (data) {
          data.Contents?.forEach((element) => {
            const sorg = element.Key;
            const dest = element.Key?.replace(sourcePath, destinationPath);
            if (dest) {
              const params3 = {
                Bucket: BUCKET_NAME,
                CopySource: `${BUCKET_NAME}/${sorg}`,
                Key: dest,
              };
              const promise = bucket?.copyObject(params3).promise();
              promise && promises.push(promise);
            }
          });
        }
      }

      return promises;
    } catch (error) {
      console.error("Error copying folder:", error);
      return [];
    }
  };

  const renameFolder = async (folderName: string, sourcePath: string) => {
    try {
      const promises = await copyFolder(sourcePath, folderName);
      const result = await Promise.all(promises);
      await deleteFolder(sourcePath);
      return Boolean(result);
    } catch (error) {
      return false;
    }
  };

  const renameFile = async (
    fileName: string,
    sourcePath: string,
    withBucketName?: boolean
  ) => {
    var params2 = {
      Bucket: BUCKET_NAME,
      CopySource: `${BUCKET_NAME}/${sourcePath}`,
      Key: withBucketName ? fileName : `${breadcrumb}${fileName}`,
    };

    var params4 = {
      Bucket: BUCKET_NAME,
      Key: sourcePath,
    };

    const bucket = await refreshBucket();
    try {
      await bucket
        ?.copyObject(params2)
        .promise()
        .then(() => {
          return bucket.deleteObject(params4).promise();
        });
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleSortByProperty = (property: SortProperty) => {
    let newSort: SortStatus | null = null;
    if (property !== sortStatus.property) {
      newSort = { property, order: "asc" };
    } else {
      newSort = {
        property,
        order: sortStatus.order === "asc" ? "desc" : "asc",
      };
    }
    setSortStatus(newSort);
    if (content) {
      const sortedContent = sortContent(content, newSort);
      setContent(sortedContent);
    }
  };

  return (
    <BucketContext.Provider
      value={{
        loading,
        content,
        bucketSummary,
        breadcrumb,
        apiHandler,
        // Utils
        sortStatus,
        handleSortByProperty,
        // API
        fetchHomePageData,
        fetchBucketSummary,
        fetchBucket,
        refreshContents,
        // file
        checkFileExist,
        uploadFiles,
        deleteFile,
        getPresignedUrl,
        // folder
        createFolder,
        deleteFolder,
        renameFolder,
        renameFile,
      }}
    >
      <>{children}</>
    </BucketContext.Provider>
  );
};
